import React, { useMemo, useState } from 'react';
import {
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  Edit,
  Users,
} from 'lucide-react';
import { Model, Agent } from '../../types';
import Tooltip from '../common/Tooltip';

interface ModelTableProps {
  models: Model[];
  agents: Agent[];
  sortConfig: {
    field: 'class_name' | 'created_at' | 'updated_at';
    direction: 'asc' | 'desc';
  };
  onSort: (field: 'class_name' | 'created_at' | 'updated_at') => void;
  onView: (model: Model) => void;
  onEdit: (model: Model) => void;
  onDelete: (model: Model) => void;
  // AC-1213: Clicking an agent pill opens that agent's details modal in place
  onViewAgent?: (agent: Agent) => void;
  // AC-385: Registry stats for displaying badge with entry count
  registryStats?: Record<number, number | null>;
}

const ModelTable: React.FC<ModelTableProps> = ({
  models,
  agents,
  sortConfig,
  onSort,
  onView,
  onEdit,
  onDelete,
  onViewAgent,
  registryStats = {},
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Opens the agent details modal in place via the parent-provided handler.
  // Stops propagation so the surrounding row click handlers do not fire.
  const handleAgentClick = (e: React.MouseEvent, agent: Agent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onViewAgent) {
      onViewAgent(agent);
    }
  };

  const toggleRow = (modelId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(modelId)) {
        next.delete(modelId);
      } else {
        next.add(modelId);
      }
      return next;
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getSortIcon = (field: 'class_name' | 'created_at' | 'updated_at') => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className='w-4 h-4' />
    ) : (
      <ChevronDown className='w-4 h-4' />
    );
  };

  // Basic Python types that are NOT references to other schemas.
  // Must be kept in sync with backend/crud/crud_model.py::validate_model_dependencies.
  const BASIC_TYPES = new Set([
    'int', 'bool', 'str', 'float', 'list', 'dict', 'tuple', 'set',
  ]);

  // Extract referenced schema class names from a property type string.
  // Handles: "CategorySummary", "list[CategorySummary]", "List[CategorySummary]".
  // Skips basic types and Literal[...] values.
  const extractReferencedClassNames = (propertyType: string): string[] => {
    if (!propertyType) return [];
    if (/^Literal\[/.test(propertyType)) return [];

    const listMatch = propertyType.match(/^list\[(.+)\]$/i);
    if (listMatch) {
      const inner = listMatch[1].trim();
      return BASIC_TYPES.has(inner) ? [] : [inner];
    }

    return BASIC_TYPES.has(propertyType) ? [] : [propertyType];
  };

  // Build a reverse dependency index: for each model_id, the set of
  // ancestor model_ids whose properties reference it (directly or transitively).
  // If agent A outputs DomainSummaryResults which references CategorySummary,
  // then ancestorsByModelId[CategorySummary.id] will include DomainSummaryResults.id,
  // so A is reported as "using" CategorySummary.
  const ancestorsByModelId = useMemo(() => {
    const classNameToId = new Map<string, number>();
    for (const model of models) {
      classNameToId.set(model.class_name, model.model_id);
    }

    // Direct parents: model_id -> set of parent model_ids that reference it via properties
    const directParents = new Map<number, Set<number>>();
    for (const model of models) {
      const referencedIds = new Set<number>();
      for (const propertyType of Object.values(model.properties || {})) {
        // Rich property shape (AC-1251) may be either a string or an object
        // with a `type` field. Normalize to the type string for class name
        // extraction.
        const typeStr =
          typeof propertyType === 'string'
            ? propertyType
            : String((propertyType as any)?.type ?? '');
        for (const className of extractReferencedClassNames(typeStr)) {
          const referencedId = classNameToId.get(className);
          if (referencedId !== undefined && referencedId !== model.model_id) {
            referencedIds.add(referencedId);
          }
        }
      }
      for (const childId of referencedIds) {
        if (!directParents.has(childId)) {
          directParents.set(childId, new Set());
        }
        directParents.get(childId)!.add(model.model_id);
      }
    }

    // Transitive closure via BFS on the direct-parents graph
    const ancestors = new Map<number, Set<number>>();
    for (const model of models) {
      const visited = new Set<number>();
      const queue: number[] = [model.model_id];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        const parents = directParents.get(currentId);
        if (!parents) continue;
        for (const parentId of parents) {
          if (!visited.has(parentId)) {
            visited.add(parentId);
            queue.push(parentId);
          }
        }
      }
      ancestors.set(model.model_id, visited);
    }

    return ancestors;
  }, [models]);

  const getAgentsUsingModel = (modelId: number): Agent[] => {
    const candidateModelIds = new Set<number>([modelId]);
    const ancestors = ancestorsByModelId.get(modelId);
    if (ancestors) {
      for (const ancestorId of ancestors) {
        candidateModelIds.add(ancestorId);
      }
    }
    return agents.filter(
      agent =>
        agent.output_model_id !== undefined &&
        agent.output_model_id !== null &&
        candidateModelIds.has(agent.output_model_id)
    );
  };

  // AC-385: Render registry badge for models with registry_enabled=true
  const renderRegistryBadge = (model: Model) => {
    if (!model.registry_enabled) {
      return null;
    }

    const count = registryStats[model.model_id];
    // count is null if API call failed, undefined if not yet loaded
    const displayCount = count === null ? '--' : (count ?? '--');

    return (
      <span
        data-testid={`registry-badge-${model.model_id}`}
        className='ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700'
      >
        Registry: {displayCount}
      </span>
    );
  };

  return (
    <div className='space-y-4'>
      {/* Desktop View */}
      <div className='hidden lg:block overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg'>
        <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
          <thead className='bg-gray-50 dark:bg-gray-900'>
            <tr>
              <th
                scope='col'
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                onClick={() => onSort('class_name')}
              >
                <div className='flex items-center gap-2'>
                  Schema Name
                  {getSortIcon('class_name')}
                </div>
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
              >
                Description
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
              >
                Properties
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
              >
                <div className='flex items-center gap-2'>
                  <Users className='w-4 h-4' />
                  Used By Agents
                </div>
              </th>
              <th
                scope='col'
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                onClick={() => onSort('updated_at')}
              >
                <div className='flex items-center gap-2'>
                  Updated
                  {getSortIcon('updated_at')}
                </div>
              </th>
              <th scope='col' className='relative px-6 py-3'>
                <span className='sr-only'>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className='bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700'>
            {models.map(model => (
              <React.Fragment key={model.model_id}>
                <tr
                  className='hover:bg-primary-50 dark:hover:bg-primary-900/20 dark:bg-gray-900 cursor-pointer transition-colors'
                  onClick={() => toggleRow(model.model_id)}
                >
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='flex items-center gap-2'>
                      {expandedRows.has(model.model_id) ? (
                        <ChevronDown className='w-4 h-4 text-gray-400' />
                      ) : (
                        <ChevronRight className='w-4 h-4 text-gray-400' />
                      )}
                      <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                        {model.class_name}
                      </div>
                      {renderRegistryBadge(model)}
                    </div>
                  </td>
                  <td className='px-6 py-4'>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                      {truncateText(model.description)}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                      {Object.keys(model.properties).length} properties
                    </div>
                  </td>
                  <td className='px-6 py-4'>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                      {(() => {
                        const usingAgents = getAgentsUsingModel(model.model_id);
                        if (usingAgents.length === 0) {
                          return (
                            <span className='text-gray-400'>No agents</span>
                          );
                        }
                        return (
                          <div>
                            {usingAgents.map((agent, index) => (
                              <button
                                type='button'
                                key={agent.agent_id}
                                onClick={(e) => handleAgentClick(e, agent)}
                                title={`View details for ${agent.name}`}
                                className='text-xs bg-primary-50 dark:bg-primary-900/30 text-blue-700 dark:text-blue-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 px-2 py-1 rounded w-fit block cursor-pointer text-left'
                                style={{ marginBottom: '4px' }}
                              >
                                {agent.name}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                      {formatDate(model.updated_at)}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                    <div className='flex items-center justify-end gap-2'>
                      <div className='flex items-center space-x-2'>
                        <Tooltip content='View details'>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              onView(model);
                            }}
                            className='p-1 text-gray-400 hover:text-primary-600 transition-colors'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                        </Tooltip>
                        <Tooltip content='Edit schema'>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              onEdit(model);
                            }}
                            className='p-1 text-gray-400 hover:text-yellow-600 transition-colors'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                        </Tooltip>
                        <Tooltip content='Delete schema'>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              onDelete(model);
                            }}
                            className='p-1 text-gray-400 hover:text-red-600 transition-colors'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(model.model_id) && (
                  <tr>
                    <td
                      colSpan={6}
                      className='px-6 py-4 bg-gray-50 dark:bg-gray-900'
                    >
                      <div className='space-y-4'>
                        {model.description && (
                          <div>
                            <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                              Description
                            </h4>
                            <p className='text-sm text-gray-500 dark:text-gray-400'>
                              {model.description}
                            </p>
                          </div>
                        )}
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                            Properties
                          </h4>
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {Object.entries(model.properties).map(
                              ([name, typeString]) => {
                                // AC-1251: properties values may be rich objects.
                                const typeStr =
                                  typeof typeString === 'string'
                                    ? typeString
                                    : String((typeString as any)?.type ?? '');
                                const parsedType =
                                  parsePropertyType(typeStr);
                                return (
                                  <div
                                    key={name}
                                    className='bg-white dark:bg-cap-dark p-3 rounded-lg border border-gray-200 dark:border-gray-700'
                                  >
                                    <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                      {name}
                                    </div>
                                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                                      {parsedType.type === 'Literal' &&
                                      parsedType.literalValues
                                        ? `Literal[${parsedType.literalValues.join(', ')}]`
                                        : parsedType.type}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                        {model.relations &&
                          Object.keys(model.relations).length > 0 && (
                            <div>
                              <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                                Relations
                              </h4>
                              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {Object.entries(model.relations).map(
                                  ([name, type]) => (
                                    <div
                                      key={name}
                                      className='bg-white dark:bg-cap-dark p-3 rounded-lg border border-gray-200 dark:border-gray-700'
                                    >
                                      <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                        {name}
                                      </div>
                                      <div className='text-sm text-gray-500 dark:text-gray-400'>
                                        {type}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                            Used By Agents
                          </h4>
                          {(() => {
                            const usingAgents = getAgentsUsingModel(
                              model.model_id
                            );
                            if (usingAgents.length === 0) {
                              return (
                                <div className='text-sm text-gray-500 dark:text-gray-400 italic'>
                                  No agents are currently using this schema
                                </div>
                              );
                            }
                            return (
                              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {usingAgents.map(agent => (
                                  <button
                                    type='button'
                                    key={agent.agent_id}
                                    onClick={(e) => handleAgentClick(e, agent)}
                                    title={`View details for ${agent.name}`}
                                    className='text-left bg-white dark:bg-cap-dark p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors cursor-pointer'
                                  >
                                    <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                      {agent.name}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View */}
      <div className='lg:hidden'>
        <div className='divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden'>
          {models.map(model => {
            const usingAgents = getAgentsUsingModel(model.model_id);

            return (
              <div
                key={model.model_id}
                className='bg-white dark:bg-cap-dark hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors'
              >
                <div
                  className='p-4 cursor-pointer'
                  onClick={() => toggleRow(model.model_id)}
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex items-start gap-2 flex-1 min-w-0'>
                      <button className='mt-0.5 text-gray-400 flex-shrink-0'>
                        {expandedRows.has(model.model_id) ? (
                          <ChevronDown className='w-4 h-4' />
                        ) : (
                          <ChevronRight className='w-4 h-4' />
                        )}
                      </button>
                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center flex-wrap gap-1 mb-1'>
                          <Tooltip content={model.class_name} position='bottom'>
                          <h3
                            className='text-sm font-medium text-gray-900 dark:text-warm-white truncate'
                          >
                            {model.class_name}
                          </h3>
                        </Tooltip>
                          {renderRegistryBadge(model)}
                        </div>
                        {model.description && (
                          <p className='text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2'>
                            {model.description}
                          </p>
                        )}
                        <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400'>
                          <span className='inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'>
                            {Object.keys(model.properties).length} properties
                          </span>
                          {Object.keys(model.relations || {}).length > 0 && (
                            <span className='inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'>
                              {Object.keys(model.relations || {}).length}{' '}
                              relations
                            </span>
                          )}
                          {usingAgents.length > 0 && (
                            <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'>
                              <Users className='w-3 h-3' />
                              {usingAgents.length} agent
                              {usingAgents.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          <span>Updated: {formatDate(model.updated_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className='flex items-center space-x-1 ml-2 flex-shrink-0'>
                      <Tooltip content='View details'>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onView(model);
                          }}
                          className='p-2 text-gray-400 hover:text-primary-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center'
                        >
                          <Eye className='w-4 h-4' />
                        </button>
                      </Tooltip>
                      <Tooltip content='Edit schema'>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onEdit(model);
                          }}
                          className='p-2 text-gray-400 hover:text-yellow-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center'
                        >
                          <Edit className='w-4 h-4' />
                        </button>
                      </Tooltip>
                      <Tooltip content='Delete schema'>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onDelete(model);
                          }}
                          className='p-2 text-gray-400 hover:text-red-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center'
                        >
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
                {expandedRows.has(model.model_id) && (
                  <div className='px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-3'>
                    {model.description && (
                      <div>
                        <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                          Description
                        </h4>
                        <p className='text-sm text-gray-500 dark:text-gray-400'>
                          {model.description}
                        </p>
                      </div>
                    )}
                    <div>
                      <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                        Properties
                      </h4>
                      <div className='space-y-2'>
                        {Object.entries(model.properties).map(
                          ([name, typeString]) => {
                            // AC-1251: properties values may be rich objects.
                            const typeStr =
                              typeof typeString === 'string'
                                ? typeString
                                : String((typeString as any)?.type ?? '');
                            const parsedType = parsePropertyType(typeStr);
                            return (
                              <div
                                key={name}
                                className='bg-gray-50 dark:bg-gray-900 p-3 rounded-lg'
                              >
                                <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                  {name}
                                </div>
                                <div className='text-sm text-gray-500 dark:text-gray-400'>
                                  {parsedType.type === 'Literal' &&
                                  parsedType.literalValues
                                    ? `Literal[${parsedType.literalValues.join(', ')}]`
                                    : parsedType.type}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                    {model.relations &&
                      Object.keys(model.relations).length > 0 && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                            Relations
                          </h4>
                          <div className='space-y-2'>
                            {Object.entries(model.relations).map(
                              ([name, type]) => (
                                <div
                                  key={name}
                                  className='bg-gray-50 dark:bg-gray-900 p-3 rounded-lg'
                                >
                                  <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                    {name}
                                  </div>
                                  <div className='text-sm text-gray-500 dark:text-gray-400'>
                                    {type}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    <div>
                      <h4 className='text-sm font-medium text-gray-900 dark:text-warm-white mb-1'>
                        Used By Agents
                      </h4>
                      {usingAgents.length === 0 ? (
                        <div className='text-sm text-gray-500 dark:text-gray-400 italic'>
                          No agents are currently using this schema
                        </div>
                      ) : (
                        <div className='space-y-2'>
                          {usingAgents.map(agent => (
                            <button
                              type='button'
                              key={agent.agent_id}
                              onClick={(e) => handleAgentClick(e, agent)}
                              title={`View details for ${agent.name}`}
                              className='w-full text-left bg-gray-50 dark:bg-gray-900 p-3 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors cursor-pointer'
                            >
                              <div className='text-sm font-medium text-gray-900 dark:text-warm-white'>
                                {agent.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModelTable;

// Helper function to parse the property type string
const parsePropertyType = (typeString: string) => {
  if (typeString.startsWith('Literal[')) {
    const literalValues = typeString
      .slice(8, -1)
      .split(',')
      .map(v => v.trim().replace(/['"]/g, ''));
    return { type: 'Literal', literalValues };
  }
  return { type: typeString };
};
